import { defineStore } from "pinia";
import { Quotation } from "./entity";
import { QuotationList } from "./list.gen";
import type { QuotationStore, QuotationStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const quotationList = new QuotationList();
export const useQuotationStore = defineStore("quotationStore", {
  state: (): QuotationStoreState => ({
    activeID: null,
    cache: {},
    list: [] as Quotation[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-Quotation", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.Quotation"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(quotation: Quotation): Promise<void> {
      // Set active
      await quotation
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = quotation.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(quotation: Quotation): void {
      const id = quotation.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(quotation);

        // Set active
        if (this.active === null) {
          this.activeID = quotation.id;
        }
      } else {
        this.reinsert(quotation);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as Quotation[]).map((x: any) => {
        const quotation = new Quotation({});
        quotation.set(x as JSONData);
        return quotation;
      });
    },
    async delete(quotation: Quotation): Promise<void> {
      await quotation
        .delete()
        .then(() => {
          this.remove(quotation);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const quotation = (this.list as Quotation[]).find(
        (x: Quotation) => x.id === id,
      );
      if (quotation !== undefined) {
        await this.delete(quotation);
      } else {
        throw new Error("Could not find Quotation with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as Quotation[]).some((x: Quotation) => x.id === id);
    },
    get(id: string): Quotation | null {
      return (
        (this.list as Quotation[]).find((x: Quotation) => x.id === id) || null
      );
    },
    reinsert(quotation: Quotation): void {
      const index = (this.list as Quotation[]).findIndex(
        (x: Quotation) => x.id === quotation.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          quotation,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(quotation);
      }
      this.updateKey += 1;
    },
    remove({ id }: Quotation): void {
      const index = (this.list as Quotation[]).findIndex(
        (x: Quotation) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<Quotation> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const quotation = this.get(id);
        if (quotation !== null) {
          return quotation;
        }
      }

      // Retrieve quotation
      const quotation = new Quotation({});

      // Get quotation
      await quotation.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate quotation
        await this.activate(entity);
      });

      return quotation;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: Quotation[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as Quotation[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await quotationList.getAll().then(async (list) => {
        list.forEach((quotation) => {
          this.reinsert(quotation);
        });

        // Activate quotation
        if (list.length > 0) {
          if (quotationList.active !== null) {
            this.activeID = quotationList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("quotation_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as Quotation[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Quotation[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as Quotation),
        };
      }

      // Retrieve list
      const items = await quotationList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((quotation) => {
            this.reinsert(quotation);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Quotation) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Quotation[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as Quotation),
        };
      }

      // Retrieve list
      const items = await quotationList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((quotation) => {
            this.reinsert(quotation);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Quotation) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: Quotation[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as Quotation),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await quotationList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((quotation) => {
            this.reinsert(quotation);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: Quotation) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(quotation: Quotation): Promise<string> {
      // Update quotation
      await quotation.update();

      // Reinsert quotation
      this.reinsert(quotation);

      // Activate quotation
      if (this.active === null) {
        await this.activate(quotation);
      }

      this.emitStoreUpdateEvent();

      return quotation.id;
    },
  },
  getters: {
    active(): Quotation | null {
      if (this.activeID === null) {
        return null;
      }

      const quotation = (this.list as Quotation[]).find(
        (x: Quotation) => x.id === this.activeID,
      );
      if (quotation === undefined) {
        return null;
      }

      return quotation;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createQuotationStore(): QuotationStore {
  const store = useQuotationStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as QuotationStore; // TODO: Fix this
}
